import { type ClipboardEvent, useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Field, FieldLabel } from "@volca/ui/components/ui/field";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { formatDateRange } from "@/core/utils/date";
import { NoteGrid } from "./note-grid";
import type { PayrollNoteComposerState } from "./use-note-composer";

export const PayrollNoteComposer = ({
  composer,
}: {
  composer: PayrollNoteComposerState;
}) => {
  const { body, gridText, error, nextPeriod, isPending } = composer;
  const noteId = useId();

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const text = event.clipboardData.getData("text/plain");
    const html = event.clipboardData.getData("text/html");
    if (!text || (!text.includes("\t") && !/<table[\s>]/i.test(html))) return;
    event.preventDefault();
    composer.setGridText(text);
  };

  return (
    <Dialog open={composer.isOpen} onOpenChange={composer.setOpen}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] min-w-0 grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Note for next open period</DialogTitle>
          <DialogDescription>
            {nextPeriod
              ? formatDateRange(nextPeriod.start_date, nextPeriod.end_date)
              : composer.isCheckingPeriod
                ? "Checking the next open pay period…"
                : "The next open pay period is unavailable."}
          </DialogDescription>
        </DialogHeader>
        <Stack gap={3} className="min-w-0">
          {composer.periodError && (
            <Stack direction="row" align="center" gap={2}>
              <Text role="alert" tone="destructive">
                {composer.periodError}
              </Text>
              <Button
                size="xs"
                variant="outline"
                loading={composer.isCheckingPeriod}
                onClick={composer.retryPeriod}
              >
                Retry
              </Button>
            </Stack>
          )}
          <Field>
            <FieldLabel htmlFor={noteId}>Payroll note</FieldLabel>
            <Textarea
              id={noteId}
              placeholder="Enter a note or paste copied grid cells"
              value={body}
              maxLength={10_000}
              rows={4}
              disabled={isPending}
              onChange={(event) => composer.setBody(event.target.value)}
              onPaste={handlePaste}
            />
          </Field>
          {gridText !== null && (
            <Stack gap={2} className="min-w-0">
              <Stack direction="row" justify="between" align="center">
                <Text variant="label">Cells</Text>
                <Button
                  size="xs"
                  variant="ghost"
                  disabled={isPending}
                  onClick={() => composer.setGridText(null)}
                >
                  Remove cells
                </Button>
              </Stack>
              <NoteGrid text={gridText} />
              <Text variant="caption" tone="muted">
                Paste another range to replace these cells.
              </Text>
            </Stack>
          )}
          {error && (
            <Text role="alert" tone="destructive">
              {error}
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            disabled={isPending}
            onClick={() => composer.setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            loading={isPending}
            disabled={
              isPending ||
              !composer.isAvailable ||
              (!body.trim() && !gridText?.trim())
            }
            onClick={composer.save}
          >
            Save for next open period
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
